import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import ContactForm from '../../components/ContactForm';
import Icon from '../../components/AppIcon';

const ContactPage = () => {
  const handleContactSuccess = (data) => {
    // Could add analytics tracking here
    console.log('Contact form submitted successfully:', data);
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Contact Us - Solis Games</title>
        <meta name="description" content="Get in touch with Solis Games support. Report issues, ask questions, or provide feedback about our gaming platform." />
      </Helmet>
      
      <Header />
      
      <main className="pt-16 pb-20 md:pb-8">
        <div className="container mx-auto px-4 lg:px-8 py-8 max-w-4xl">
          {/* Page Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="MessageSquare" size={32} className="text-primary" />
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">Contact Support</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Need help? Have a question? We're here to assist you. Send us a message and we'll get back to you as soon as possible.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <ContactForm
                onSuccess={handleContactSuccess}
                sourcePage="Contact Page"
                sourceUrl="/contact"
              />
            </div>

            {/* Sidebar with additional info */}
            <div className="space-y-6">
              {/* Quick Help */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <Icon name="Zap" size={20} className="text-primary mr-2" />
                  Quick Help
                </h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Common Issues</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Games not loading? Check your browser compatibility</li>
                      <li>• Billing questions? Visit subscription management</li>
                      <li>• Account issues? Try signing out and back in</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Before Contacting</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Clear your browser cache</li>
                      <li>• Disable browser extensions</li>
                      <li>• Check your internet connection</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Response Times */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <Icon name="Clock" size={20} className="text-primary mr-2" />
                  Response Times
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">General Inquiries</span>
                    <span className="text-sm font-medium text-foreground">24-48 hours</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Technical Issues</span>
                    <span className="text-sm font-medium text-foreground">12-24 hours</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Billing Support</span>
                    <span className="text-sm font-medium text-foreground">4-8 hours</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Security Issues</span>
                    <span className="text-sm font-medium text-primary">Priority</span>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-foreground mb-8 text-center">
              Frequently Asked Questions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-semibold text-foreground mb-3">
                  How do I report a bug in a game?
                </h3>
                <p className="text-sm text-muted-foreground">
                  Use the contact form above and select "Game Bug" as the issue type. 
                  Please include details about what you were doing when the bug occurred 
                  and your browser information.
                </p>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-semibold text-foreground mb-3">
                  I'm having trouble with my subscription
                </h3>
                <p className="text-sm text-muted-foreground">
                  For billing and subscription issues, select "Billing & Subscription" 
                  when contacting us. You can also visit your subscription management 
                  page to view and manage your subscription.
                </p>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-semibold text-foreground mb-3">
                  How do I report inappropriate content?
                </h3>
                <p className="text-sm text-muted-foreground">
                  Select "Content Report" when submitting your message. Please provide 
                  specific details about the content and where you encountered it so 
                  we can investigate quickly.
                </p>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-semibold text-foreground mb-3">
                  Can I track my support request?
                </h3>
                <p className="text-sm text-muted-foreground">
                  If you're signed in when you submit a contact form, you'll receive
                  notifications about updates to your request. You can view all your
                  support tickets and responses in your profile under the Support Tickets tab.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ContactPage;
